import { ShieldOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export type MonitorStatus = 'live' | 'parked' | 'redirect_in_zone' | 'redirect_out_of_zone' | 'error';

export const OVERRIDE_STATUS_OPTIONS: MonitorStatus[] = [
  'live',
  'parked',
  'redirect_in_zone',
  'redirect_out_of_zone',
  'error',
];

const SWATCH_COLORS: Record<MonitorStatus, string> = {
  live: 'bg-green-500',
  parked: 'bg-slate-400',
  redirect_in_zone: 'bg-blue-500',
  redirect_out_of_zone: 'bg-amber-500',
  error: 'bg-red-500',
};

const TEXT_COLORS: Record<MonitorStatus, string> = {
  live: 'text-green-600',
  parked: 'text-slate-500',
  redirect_in_zone: 'text-blue-600',
  redirect_out_of_zone: 'text-amber-600',
  error: 'text-red-600',
};

/**
 * Grid cell for the per-domain manual status override. Overrides live in
 * `monitorStatusOverrides` and take precedence over scan results everywhere the
 * monitor computes an effective status.
 */
export function OverrideCell({
  domain,
  hasOverride,
  overrideStatus,
  onSetOverride,
  onClearOverride,
}: {
  domain: string;
  hasOverride: boolean;
  overrideStatus: MonitorStatus | null;
  onSetOverride: (domain: string, status: MonitorStatus) => void;
  onClearOverride: (domain: string) => void;
}) {
  const textColor = overrideStatus ? TEXT_COLORS[overrideStatus] ?? 'text-muted-foreground' : 'text-muted-foreground';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`h-7 gap-1 px-2 ${hasOverride ? textColor : 'text-muted-foreground'}`}
        >
          {hasOverride && overrideStatus
            ? (
                <>
                  <div className={`size-2.5 rounded-sm ${SWATCH_COLORS[overrideStatus] ?? 'bg-gray-300'}`} />
                  <span className="text-xs">{overrideStatus.replaceAll('_', ' ')}</span>
                </>
              )
            : (
                <>
                  <ShieldOff className="size-3" />
                  <span className="text-xs">None</span>
                </>
              )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
          Set Override
        </div>
        {OVERRIDE_STATUS_OPTIONS.map((status) => (
          <DropdownMenuItem
            key={status}
            onClick={() => onSetOverride(domain, status)}
            className="gap-2"
          >
            <div className={`size-2 rounded-full ${SWATCH_COLORS[status] ?? 'bg-gray-300'}`} />
            {status.replaceAll('_', ' ')}
            {overrideStatus === status && <span className="ml-auto">✓</span>}
          </DropdownMenuItem>
        ))}
        {hasOverride && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onClearOverride(domain)}
              className="text-red-600"
            >
              <ShieldOff className="mr-2 size-3" />
              Clear Override
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
